import { getRuleTypeBucket } from 'utils/ruleTypeBucket';

// Reshapes Prime-MOQ-Backend's RuleCoverageDto (POST /rules/coverage,
// GET /rules/:id/coverage — see rule-precedence.service.ts there and
// docs/rule-precedence-implementation-plan.md §0/§4.3 here) into what the
// coverage footer (components/rules/scope/CoverageFooter.js), the save review
// (components/rules/SaveReviewModal.js) and the rules list read.
//
// A rule's scope is VARIANT-granular (RuleProduct rows carry variantId, and
// arbitration picks a winner per variant, not per product — a product can
// have some variants enforcing and others lost to a different rule). So
// *Variants fields are the PRIMARY numbers everywhere in this app's coverage
// and overlap UI; *Products fields are secondary context only ("across N
// products") — never the headline count. See CLAUDE.md-adjacent note: this
// was a real correction after a first pass showed product counts as primary.
//
// Reason codes stay codes — the UI turns them into sentences via i18n
// (`rule.coverage.reason.<code>`), so the backend never ships English.

export const EMPTY_COVERAGE = {
  resolvable: true,
  // 'ready' | 'loading' — see the note in coverageAdaptor below.
  status: 'ready',
  pendingCollections: [],
  missingCollections: [],
  scopeProducts: 0,
  scopeVariants: 0,
  enforcingProducts: 0,
  enforcingVariants: 0,
  overriddenProducts: 0,
  overriddenVariants: 0,
  groups: [],
  // Rules this rule would take variants from when saved (see the backend's
  // CoverageResult.displaces). Empty when it displaces nobody.
  displaces: [],
  groupsHash: '',
  stackAcrossTypes: false,
};

export function coverageAdaptor(dto) {
  if (!dto) return EMPTY_COVERAGE;
  const scopeProducts = dto.scopeProducts ?? 0;
  const scopeVariants = dto.scopeVariants ?? 0;
  const enforcingProducts = dto.enforcingProducts ?? 0;
  const enforcingVariants = dto.enforcingVariants ?? 0;
  // `overridden*` means LIVE, unresolved conflicts (rule-precedence.
  // service.ts's computeCoverage doc comment has the full "gross vs net"
  // story), NOT scope minus enforcing, which now also counts this rule's
  // OWN excludes (manual or automatic), a settled state, not a problem.
  // Trust the DTO's own overridden* first (the backend already derives it
  // correctly from `groups`), falling back to summing `groups` here only
  // for an older backend that hasn't shipped the field yet (mid-rollout),
  // same reasoning the original "never trust a raw field defaulting via ??"
  // comment here used to warn about, except the fallback below is ALSO
  // live-conflict-based, not the old (wrong, gross-based) subtraction, so
  // it can't silently reintroduce the "always 100%" bug this was fixing.
  const groups = dto.groups ?? [];
  const liveOverriddenProducts = groups.reduce((sum, g) => sum + (g.productCount ?? 0), 0);
  const liveOverriddenVariants = groups.reduce((sum, g) => sum + (g.variantCount ?? 0), 0);
  return {
    resolvable: dto.resolvable !== false,
    // Are these numbers final?
    //
    // `loading` means part of the scope had not been fetched when the backend
    // counted — a collection whose membership has never been mirrored, say. The
    // counts are then a LOWER BOUND and must not be rendered: "0 of 0 variants
    // enforce this rule" reads as a verdict, and a merchant who sees it
    // concludes their rule matches nothing and abandons it. Show progress
    // instead and re-ask.
    //
    // Defaults to 'ready' so an older backend that does not send the field
    // behaves exactly as before rather than sticking on a spinner.
    status: dto.status === 'loading' ? 'loading' : 'ready',
    pendingCollections: dto.pendingCollections ?? [],
    // Collections the rule points at that no longer exist in Shopify. A
    // SETTLED answer, not a slow one — waiting will never resolve it, so the
    // UI names them and lets the merchant remove them instead of spinning.
    missingCollections: dto.missingCollections ?? [],
    scopeProducts,
    scopeVariants,
    enforcingProducts,
    enforcingVariants,
    overriddenProducts: dto.overriddenProducts ?? liveOverriddenProducts,
    overriddenVariants: dto.overriddenVariants ?? liveOverriddenVariants,
    groups: groups.map((g) => ({
      byRule: {
        id: g.byRule?.id,
        name: g.byRule?.name ?? '',
        type: g.byRule?.type,
        typeBucket: getRuleTypeBucket(g.byRule?.type),
        scopeType: g.byRule?.scopeType,
      },
      reason: g.reason,
      productCount: g.productCount ?? 0,
      variantCount: g.variantCount ?? 0,
      sample: (g.sample ?? []).map((p) => ({ id: p.id, title: p.title, image: p.image ?? null, variantIds: p.variantIds ?? [] })),
    })),
    displaces: (dto.displaces ?? []).map((d) => ({
      rule: { id: d.rule?.id, name: d.rule?.name ?? '', type: d.rule?.type, scopeType: d.rule?.scopeType },
      productCount: d.productCount ?? 0,
      variantCount: d.variantCount ?? 0,
    })),
    groupsHash: dto.groupsHash ?? '',
    stackAcrossTypes: Boolean(dto.stackAcrossTypes),
  };
}

// Full / Partial / Inactive for the rules list (docs/rule-precedence-ux.html,
// "Rules list" tab), by VARIANT — a product with a mix of enforcing and lost
// variants would misreport as "Full" or "Inactive" under a product-based
// verdict. `null` scope = never resolved yet.
//
// Computed for a paused/scheduled/expired rule too, not just an active one:
// the persisted counts (list.adaptor.js's scopeVariants/enforcingVariants)
// still describe what this rule would cover if it were on, and the merchant
// asked to see it: "چرا با همین عناوین توی دی‌اکتیوها بج کاوریجشونم نشون
// ندیم" (why not show the same badges for the deactivated ones too). The
// badge keeps its normal color either way (product's own call): it's
// RuleRow.js's progress BAR that mutes to gray for a non-active row instead,
// not this verdict.
//
// A rule whose OWN scope resolves to zero variants (0 of 0, not "0 of N")
// still returns 'inactive', not null: confirmed live the merchant expects
// the same red badge here as any other rule enforcing nothing, deactivated
// or not, rather than a silent gap in the column. (An earlier version
// special-cased this to null on the reasoning that the Status column's own
// noProductsTooltip already explains a 0-product ACTIVE rule, true, but
// that reasoning doesn't cover an ordinary deactivated rule with an empty
// scope, which has no such tooltip and would show nothing at all here.)
//
// Partial/Full compares enforcingVariants to scopeVariants (the rule's GROSS
// target) directly, on purpose: "338 of 340, Partial" is correct even once
// the missing 2 are a settled, formally-excluded conflict with nothing left
// to act on, the fraction itself is the true answer to "does this rule cover
// what it was asked to." That is a SEPARATE question from "is there
// something live to resolve right now", which is what `overriddenVariants`
// (LIVE, unresolved conflicts) answers instead, read directly by the
// progress-bar color and warning banner (RuleRow.js, CoverageFooter.js), not
// by this status. Deciding Partial/Full off `overriddenVariants` here was
// tried and reverted: it made a rule that had already excluded its own gap
// read "Full" despite still enforcing less than its target, which is exactly
// the misleading badge this function exists to avoid.
export function coverageStatusFor({ scopeVariants, enforcingVariants }) {
  if (scopeVariants == null || enforcingVariants == null) return null;
  if (enforcingVariants === 0) return 'inactive';
  if (enforcingVariants < scopeVariants) return 'partial';
  return 'full';
}
